// Inventory: On the floor (Starchive-Inventory-Section-Plan, section 3.1; the canvas's I1 board). Every piece on the floor
// with its photo, from the live product mirror. Filters and search run client-side over the one read; clicking a piece
// opens the drawer (PieceDrawer.tsx).
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Screen } from '../ReportsApp'
import { downloadCsv, useReport } from '../api'
import { consignorName, int, money, moneyTile, n, pct } from '../format'
import { AsyncButton, Badge, Tile } from '../ui'
import { CostChip, OwnerChip, PhotoImg, QaBadge, type FloorPiece } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'

interface FloorData {
  synced_at: string | null
  pieces: FloorPiece[]
  tiles: { pieces: number; listings: number; list_value: number; owner_recorded: number; no_owner: number; no_owner_list: number
    real_cost: number; legacy: number; typed: number; consignors: number; over_180: number; over_180_list: number; oldest_days: number
    qa_passed: number; qa_never: number; qa_sent_back: number }
}

const AGES = { 'Under 90': (d: number) => d <= 90, '91 to 180': (d: number) => d > 90 && d <= 180, 'Over 180': (d: number) => d > 180 } as const
const COSTS = { 'Real cost': 'intake', 'Legacy, unknown': 'legacy', 'Typed in Shopify': 'typed', 'Consignor, none': 'consignor' } as const
const SORTS = ['Recently listed', 'Oldest first', 'Price, high to low', 'Price, low to high', 'Brand'] as const

export function Floor() {
  const { data: d, error, loading, refresh } = useReport<FloorData>('inv_floor')
  const [view, setView] = useState<'grid' | 'table'>('grid')
  const [owner, setOwner] = useState(''); const [brand, setBrand] = useState(''); const [type, setType] = useState('')
  const [consignor, setConsignor] = useState('')
  const [age, setAge] = useState(''); const [cost, setCost] = useState(''); const [qa, setQa] = useState('')
  const [q, setQ] = useState(''); const [sort, setSort] = useState<(typeof SORTS)[number]>('Recently listed')
  const [shown, setShown] = useState(60)
  const [open, setOpen] = useState<string | null>(null)

  const brands = useMemo(() => { const m = new Map<string, number>(); for (const p of d?.pieces ?? []) m.set(p.brand || '(none)', (m.get(p.brand || '(none)') ?? 0) + 1); return [...m.entries()].sort((a, b) => b[1] - a[1]) }, [d])
  // Every consignor with pieces on the floor (Tim, 30 Sep: filter the floor by each consignor), named, A to Z.
  const consignors = useMemo(() => {
    const m = new Map<string, { pieces: number; list: number; oldest: number }>()
    for (const p of d?.pieces ?? []) {
      if (p.owner !== 'consignor') continue
      const k = p.consignor_key || '(no key)'
      const e = m.get(k) ?? { pieces: 0, list: 0, oldest: 0 }
      e.pieces += n(p.quantity) || 1; e.list += n(p.list_price) * (n(p.quantity) || 1); e.oldest = Math.max(e.oldest, n(p.days_on_floor))
      m.set(k, e)
    }
    const name = (k: string) => (k === '(no key)' ? 'Consignor, no key on the tag' : consignorName(k))
    return [...m.entries()].map(([k, e]) => ({ key: k, name: name(k), ...e })).sort((a, b) => a.name.localeCompare(b.name))
  }, [d])
  const picked = consignors.find((c) => c.key === consignor)
  const types = useMemo(() => { const m = new Map<string, number>(); for (const p of d?.pieces ?? []) m.set(p.product_type || '(none)', (m.get(p.product_type || '(none)') ?? 0) + 1); return [...m.entries()].sort((a, b) => b[1] - a[1]) }, [d])

  const rows = useMemo(() => {
    let r = d?.pieces ?? []
    if (owner) r = r.filter((p) => (owner === 'none' ? !p.owner : p.owner === owner))
    if (consignor) r = r.filter((p) => p.owner === 'consignor' && (p.consignor_key || '(no key)') === consignor)
    if (brand) r = r.filter((p) => (p.brand || '(none)') === brand)
    if (type) r = r.filter((p) => (p.product_type || '(none)') === type)
    if (age) r = r.filter((p) => AGES[age as keyof typeof AGES](n(p.days_on_floor)))
    if (cost) r = r.filter((p) => (cost === 'intake' ? ['intake', 'trade_credit'].includes(p.cost_source) : p.cost_source === cost))
    if (qa) r = r.filter((p) => (qa === 'never' ? !p.qa : p.qa === qa))
    if (q.trim()) { const w = q.trim().toLowerCase(); r = r.filter((p) => [p.title, p.brand, p.sku, p.barcode].some((x) => String(x ?? '').toLowerCase().includes(w))) }
    const by: Record<(typeof SORTS)[number], (a: FloorPiece, b: FloorPiece) => number> = {
      'Recently listed': (a, b) => String(b.on_floor_since).localeCompare(String(a.on_floor_since)),
      'Oldest first': (a, b) => n(b.days_on_floor) - n(a.days_on_floor),
      'Price, high to low': (a, b) => n(b.list_price) - n(a.list_price),
      'Price, low to high': (a, b) => n(a.list_price) - n(b.list_price),
      'Brand': (a, b) => (a.brand || 'zz').localeCompare(b.brand || 'zz') || n(b.list_price) - n(a.list_price),
    }
    return [...r].sort(by[sort])
  }, [d, owner, consignor, brand, type, age, cost, qa, q, sort])

  const t = d?.tiles
  const sel = (value: string, set: (v: string) => void, label: string, options: [string, string][]) => (
    <label className={'rp-pill' + (value ? ' is-on' : '')}>
      <span>{label}</span>
      <select value={value} onChange={(e) => { set(e.target.value); setShown(60) }} aria-label={label}>
        <option value="">All</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  )

  return (
    <Screen title="On the floor" sub={t ? `${int(t.pieces)} pieces on ${int(t.listings)} listings, live from the store` : 'Every piece on the floor, with its photo'}
      right={<AsyncButton run={() => downloadCsv('floor')} doneLabel="Downloaded">Export CSV</AsyncButton>} wide>
      {error && <div className="rp-error">{error}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Pieces on the floor" value={t ? int(t.pieces) : '…'} sub={t ? `${int(t.listings)} listings · ${moneyTile(t.list_value)} at list` : ''} />
        <Tile label="Owner recorded" value={t ? int(t.owner_recorded) : '…'} tone={t && t.no_owner ? 'amber' : 'plain'}
          sub={t ? `${int(t.no_owner)} not recorded (${moneyTile(t.no_owner_list)} at list)` : ''} badge={t ? <Badge tone={t.no_owner ? 'amber' : 'green'}>{pct(100 * t.owner_recorded / Math.max(1, t.pieces))}</Badge> : undefined} />
        <Tile label="Real cost" value={t ? int(t.real_cost) : '…'} sub={t ? `From the app's intakes. ${int(t.legacy)} legacy: cost unknown by design; ${int(t.consignors)} consignors': no cost` : ''} />
        <Tile label="Over 180 days" value={t ? int(t.over_180) : '…'} tone={t && t.over_180 ? 'red' : 'plain'} sub={t ? `${moneyTile(t.over_180_list)} at list · oldest ${int(t.oldest_days)} days` : ''} />
        <Tile label="QA passed" value={t ? int(t.qa_passed) : '…'} sub={t ? `${int(t.qa_never)} never checked · ${int(t.qa_sent_back)} sent back` : ''} />
      </div>
      <div className="rp-filterbar">
        <div className="rp-filterbar__left">
          <div className="rp-vseg" role="group" aria-label="View">
            {(['grid', 'table'] as const).map((v) => <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>{v === 'grid' ? 'Grid' : 'Table'}</button>)}
          </div>
          <label className={'rp-pill' + (owner || consignor ? ' is-on' : '')}>
            <span>Owner</span>{(picked || owner) ? <b className="rp-pill__val">{picked ? picked.name : ({ shane: 'Shane', masih: 'Masih', split: 'Split', consignor: 'All consignors', none: 'Not recorded' } as Record<string, string>)[owner] ?? owner}</b> : null}
            <select value={consignor ? 'c:' + consignor : owner} aria-label="Owner" onChange={(e) => {
              const v = e.target.value; setShown(60)
              if (v.startsWith('c:')) { setConsignor(v.slice(2)); setOwner('consignor') } else { setConsignor(''); setOwner(v) }
            }}>
              <option value="">All</option>
              <option value="shane">Shane</option>
              <option value="masih">Masih</option>
              <option value="split">Split</option>
              <optgroup label="Consignors">
                <option value="consignor">All consignors ({int(consignors.reduce((a, c) => a + c.pieces, 0))})</option>
                {consignors.map((c) => <option key={c.key} value={'c:' + c.key}>{c.name} ({int(c.pieces)})</option>)}
              </optgroup>
              <option value="none">Not recorded</option>
            </select>
          </label>
          {sel(brand, setBrand, 'Brand', brands.slice(0, 40).map(([b, c]) => [b, `${b} (${c})`]))}
          {sel(type, setType, 'Type', types.slice(0, 40).map(([b, c]) => [b, `${b} (${c})`]))}
          {sel(age, setAge, 'Age', Object.keys(AGES).map((k) => [k, k]))}
          {sel(cost, setCost, 'Cost', Object.entries(COSTS).map(([l, v]) => [v, l]))}
          {sel(qa, setQa, 'QA', [['passed', 'Passed'], ['sent_back', 'Sent back'], ['never', 'Never checked']])}
        </div>
        <div className="rp-filterbar__right">
          <input className="rp-input rp-search" type="search" placeholder="Search title, SKU or barcode" value={q} onChange={(e) => { setQ(e.target.value); setShown(60) }} aria-label="Search the floor" />
          <span className="rp-muted rp-count">{loading && !d ? '…' : `${int(rows.length)} pieces`}</span>
          {sel(sort === 'Recently listed' ? '' : sort, (v) => setSort((v || 'Recently listed') as (typeof SORTS)[number]), 'Sort', SORTS.filter((x) => x !== 'Recently listed').map((x) => [x, x]))}
        </div>
      </div>
      {picked && (
        <div className="rp-cbar">
          <span><b>{picked.name}</b>{picked.key !== '(no key)' && <span className="rp-muted"> · consignor {picked.key}</span>}</span>
          <span>{int(picked.pieces)} {picked.pieces === 1 ? 'piece' : 'pieces'} on the floor · {money(picked.list, true)} at list · oldest {int(picked.oldest)} days</span>
          {picked.key !== '(no key)' && <Link className="rp-cbar__link" to={`/statements/consignor:${picked.key}`}>Their statement</Link>}
          <button type="button" className="rp-cbar__x" onClick={() => { setConsignor(''); setOwner('') }}>Clear</button>
        </div>
      )}
      {view === 'grid' ? (
        <div className="rp-pgrid">
          {rows.slice(0, shown).map((p) => (
            <button key={p.variant_id} type="button" className="rp-ptile" onClick={() => setOpen(p.product_id)}>
              <span className="rp-ptile__pic">
                <PhotoImg url={p.image_url} title={p.title} width={480} />
                {n(p.days_on_floor) > 90 && <span className={'rp-badge rp-ptile__age rp-badge--' + (n(p.days_on_floor) > 180 ? 'red' : 'amber')}>{int(p.days_on_floor)} days</span>}
                {p.quantity > 1 && <span className="rp-badge rp-badge--grey rp-ptile__qty">×{p.quantity}</span>}
              </span>
              <span className="rp-ptile__body">
                <span className="rp-ptile__brand">{p.brand || ' '}</span>
                <span className="rp-ptile__title">{p.title}</span>
                <span className="rp-ptile__price"><b>{money(p.list_price, true)}</b><span className="rp-muted">{p.size ? `Sz ${p.size} · ` : ''}{int(p.days_on_floor)} days</span></span>
                <span className="rp-ptile__chips"><OwnerChip owner={p.owner} consignorKey={p.consignor_key} /><CostChip source={p.cost_source} cost={p.cost} shopifyCost={p.shopify_cost} /><QaBadge qa={p.qa} /></span>
              </span>
            </button>
          ))}
          {loading && !d && [...Array(12)].map((_, i) => <div key={i} className="rp-ptile rp-ptile--skel" />)}
        </div>
      ) : (
        <div className="rp-card">
          <div className="rp-table" role="table">
            <div className="rp-table__head" style={{ gridTemplateColumns: 'minmax(0, 2.4fr) 130px 90px 170px 70px 110px' }} role="row">
              {['Piece', 'Whose', 'List', 'Cost', 'On floor', 'QA'].map((h, i) => <span key={h} role="columnheader" className={i === 2 || i === 4 ? 'r' : ''}>{h}</span>)}
            </div>
            {rows.slice(0, Math.max(shown, 100)).map((p) => (
              <div key={p.variant_id} className="rp-table__row rp-table__row--link" style={{ gridTemplateColumns: 'minmax(0, 2.4fr) 130px 90px 170px 70px 110px' }} role="row" onClick={() => setOpen(p.product_id)}>
                <span role="cell" className="rp-pcell"><PhotoImg url={p.image_url} title={p.title} width={96} small /><span className="rp-pcell__text"><b>{p.title}</b><span className="rp-sub">{p.brand}{p.size ? ` · Sz ${p.size}` : ''}{p.sku ? ` · ${p.sku}` : ''}</span></span></span>
                <span role="cell"><OwnerChip owner={p.owner} consignorKey={p.consignor_key} /></span>
                <span role="cell" className="r">{money(p.list_price, true)}</span>
                <span role="cell"><CostChip source={p.cost_source} cost={p.cost} shopifyCost={p.shopify_cost} /></span>
                <span role="cell" className="r">{int(p.days_on_floor)} d</span>
                <span role="cell"><QaBadge qa={p.qa} /></span>
              </div>
            ))}
          </div>
        </div>
      )}
      {rows.length > shown && <button type="button" className="rp-btn rp-more" onClick={() => setShown((x) => x + 120)}>Show more ({int(rows.length - shown)} left)</button>}
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
    </Screen>
  )
}
